import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { isSuperSoundAdmin } from "../constants/superSoundAccess";
import { resolveSuperSoundLibraryAccess } from "../utils/superSoundLibraryAccess";
import WaveformBar from "../components/superSound/WaveformBar";
import SuperSoundAccessPanel from "../components/superSound/SuperSoundAccessPanel";
import SuperSoundSavePanel from "../components/superSound/SuperSoundSavePanel";
import { decodeWaveformPeaks, formatTime } from "../utils/waveformPeaks";
import { setPageMeta } from "../utils/pageMeta";
import { getAuthErrorMessage } from "../utils/authErrorMessage";
import "./SuperSound.css";

const ACCEPT =
  "audio/*,video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp3,.wav,.ogg,.m4a,.mp4,.mov,.avi";

const googleProvider = new GoogleAuthProvider();

function isVideoFile(file) {
  return file.type.startsWith("video/") || /\.(mp4|mov|avi|webm)$/i.test(file.name);
}

export default function SuperSound() {
  const { t } = useTranslation();
  const mediaRef = useRef(null);
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [peaks, setPeaks] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [decodeNote, setDecodeNote] = useState(null);

  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadBusy, setLoadBusy] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);

  useEffect(() => {
    setPageMeta({
      title: "Super Sound",
      documentTitle: "Super Sound | MindBlows",
      description: "Upload audio or video, scrub the waveform, and save clips to folders.",
      url: "https://mindblows.com/sound",
      robots: "noindex, nofollow",
    });
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      setAuthError(null);
      if (next) {
        if (isSuperSoundAdmin(next.email)) {
          setUser(next);
          setAuthLoading(false);
          return;
        }
        const allowed = await resolveSuperSoundLibraryAccess(next.email);
        if (!allowed) {
          setAuthError(t("sound_access_denied", { email: next.email }));
          await signOut(auth);
          setUser(null);
          setAuthLoading(false);
          return;
        }
      }
      setUser(next);
      setAuthLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setFolders([]);
      setFiles([]);
      return undefined;
    }

    const foldersQ = query(
      collection(db, "superSoundFolders"),
      where("ownerUid", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    const unsubFolders = onSnapshot(foldersQ, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setFolders(list);
      const stillSelected = list.some((f) => f.id === selectedFolderId);
      if (!stillSelected) {
        setSelectedFolderId(list.length ? list[0].id : null);
      } else if (!selectedFolderId && list.length) {
        setSelectedFolderId(list[0].id);
      }
    });

    const filesQ = query(
      collection(db, "superSoundFiles"),
      where("ownerUid", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    const unsubFiles = onSnapshot(filesQ, (snap) => {
      setFiles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubFolders();
      unsubFiles();
    };
  }, [user, selectedFolderId]);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const loadLocalFile = useCallback(
    async (file) => {
      revokeObjectUrl();
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setMediaFile(file);
      setMediaUrl(url);
      setIsVideo(isVideoFile(file));
      setPeaks(null);
      setDuration(0);
      setCurrentTime(0);
      setPlaying(false);
      setDecodeNote(null);
      setSaveNotice(null);

      const decoded = await decodeWaveformPeaks(file);
      if (decoded) {
        setPeaks(decoded);
      } else if (isVideoFile(file)) {
        setDecodeNote("Waveform preview is simplified for some video files.");
      } else {
        setDecodeNote("Could not decode waveform; using progress bar.");
      }
    },
    [revokeObjectUrl]
  );

  const loadFromStorage = useCallback(
    async (fileDoc) => {
      setLoadBusy(true);
      try {
        const storageRef = ref(storage, fileDoc.storagePath);
        const url = await getDownloadURL(storageRef);
        revokeObjectUrl();
        setMediaFile(null);
        setMediaUrl(url);
        setIsVideo((fileDoc.contentType || "").startsWith("video/"));
        setPeaks(null);
        setDuration(fileDoc.duration || 0);
        setCurrentTime(0);
        setPlaying(false);
        setDecodeNote("Loaded from library.");

        if (fileDoc.peaks?.length) {
          setPeaks(new Float32Array(fileDoc.peaks));
        }
      } finally {
        setLoadBusy(false);
      }
    },
    [revokeObjectUrl]
  );

  useEffect(() => () => revokeObjectUrl(), [revokeObjectUrl]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) loadLocalFile(file);
    e.target.value = "";
  };

  const togglePlay = async () => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) {
      try {
        await el.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const seekTo = (time) => {
    const el = mediaRef.current;
    if (!el || !Number.isFinite(time)) return;
    const clamped = Math.max(0, Math.min(duration || el.duration || 0, time));
    el.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const skip = (delta) => seekTo(currentTime + delta);

  const signIn = async () => {
    setAuthError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (isSuperSoundAdmin(result.user.email)) {
        setUser(result.user);
        return;
      }
      const allowed = await resolveSuperSoundLibraryAccess(result.user.email);
      if (!allowed) {
        await signOut(auth);
        setAuthError(t("sound_access_denied", { email: result.user.email }));
      } else {
        setUser(result.user);
      }
    } catch (err) {
      const message = getAuthErrorMessage(err);
      if (message) setAuthError(message);
    }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || !user) return;
    const docRef = await addDoc(collection(db, "superSoundFolders"), {
      name,
      ownerUid: user.uid,
      createdAt: serverTimestamp(),
      createdBy: user.email,
    });
    setSelectedFolderId(docRef.id);
    setNewFolderName("");
  };

  const saveCurrentToLibrary = async () => {
    if (!user || !mediaFile || !selectedFolderId) return;
    const folder = folders.find((f) => f.id === selectedFolderId);
    if (!folder || folder.ownerUid !== user.uid) {
      setAuthError("Choose one of your folders before saving.");
      return;
    }
    if (!mediaFile.type.startsWith("audio/")) {
      setAuthError("Only audio files can be saved to the library (video stays local).");
      return;
    }
    setSaving(true);
    setAuthError(null);
    try {
      const fileId = crypto.randomUUID();
      const safeName = mediaFile.name.replace(/[^\w.\-() ]+/g, "_");
      const storagePath = `super-sound/${user.uid}/${selectedFolderId}/${fileId}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, mediaFile, {
        contentType: mediaFile.type || "audio/mpeg",
      });

      const peakArray = peaks ? Array.from(peaks) : [];

      await addDoc(collection(db, "superSoundFiles"), {
        folderId: selectedFolderId,
        ownerUid: user.uid,
        name: mediaFile.name,
        storagePath,
        contentType: mediaFile.type || "audio/mpeg",
        size: mediaFile.size,
        duration: duration || 0,
        peaks: peakArray,
        createdAt: serverTimestamp(),
        createdBy: user.email,
      });
      setSaveNotice(t("sound_saved_ok"));
    } catch (err) {
      setAuthError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const removeLibraryFile = async (fileDoc) => {
    if (!user || fileDoc.ownerUid !== user.uid) return;
    try {
      await deleteObject(ref(storage, fileDoc.storagePath));
    } catch {
      /* object may already be gone */
    }
    await deleteDoc(doc(db, "superSoundFiles", fileDoc.id));
  };

  const folderFiles = files.filter((f) => f.folderId === selectedFolderId);

  return (
    <div className="ss-root">
      <header className="ss-header">
        <div>
          <h1>🎵 {t("sound_title")}</h1>
          <p className="ss-tagline">{t("sound_tagline")}</p>
        </div>
        <div className="ss-header-actions">
          <button
            type="button"
            className="ss-btn ss-btn-ghost"
            onClick={() => setLibraryOpen((v) => !v)}
          >
            {libraryOpen ? t("sound_hide_library") : t("sound_show_library")}
          </button>
          {user ? (
            <button type="button" className="ss-btn ss-btn-ghost" onClick={() => signOut(auth)}>
              {t("sound_sign_out", { email: user.email })}
            </button>
          ) : (
            <button
              type="button"
              className="ss-btn ss-btn-primary"
              onClick={signIn}
              disabled={authLoading}
            >
              {t("sound_sign_in")}
            </button>
          )}
        </div>
      </header>

      {authError && (
        <p className="ss-error" role="alert">
          {authError}
          {authError.includes("Authorized domains") && (
            <>
              {" "}
              <a
                className="ss-error-link"
                href="https://console.firebase.google.com/project/mindblowscom/authentication/settings"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Firebase Auth settings
              </a>
            </>
          )}
        </p>
      )}

      <div className={`ss-layout ${libraryOpen ? "" : "ss-layout--full"}`}>
        {libraryOpen && (
          <aside className="ss-library" aria-label="Saved audio library">
            <h2>{t("sound_library")}</h2>
            {!user ? (
              <p className="ss-muted">{t("sound_library_sign_in_hint")}</p>
            ) : (
              <>
                <div className="ss-folder-create">
                  <input
                    type="text"
                    placeholder={t("sound_new_folder")}
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && createFolder()}
                  />
                  <button type="button" className="ss-btn ss-btn-primary" onClick={createFolder}>
                    {t("sound_add")}
                  </button>
                </div>
                <ul className="ss-folder-list">
                  {folders.map((folder) => (
                    <li key={folder.id}>
                      <button
                        type="button"
                        className={`ss-folder-btn ${
                          selectedFolderId === folder.id ? "active" : ""
                        }`}
                        onClick={() => setSelectedFolderId(folder.id)}
                      >
                        {folder.name}
                      </button>
                    </li>
                  ))}
                </ul>
                {selectedFolderId && (
                  <ul className="ss-file-list">
                    {folderFiles.map((f) => (
                      <li key={f.id} className="ss-file-item">
                        <button
                          type="button"
                          className="ss-file-btn"
                          onClick={() => loadFromStorage(f)}
                          disabled={loadBusy}
                        >
                          {f.name}
                        </button>
                        <button
                          type="button"
                          className="ss-icon-btn"
                          aria-label={`Delete ${f.name}`}
                          onClick={() => removeLibraryFile(f)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                    {!folderFiles.length && (
                      <li className="ss-muted">{t("sound_no_files")}</li>
                    )}
                  </ul>
                )}
                {isSuperSoundAdmin(user.email) && (
                  <SuperSoundAccessPanel adminEmail={user.email} />
                )}
              </>
            )}
          </aside>
        )}

        <div className="ss-main">
          {!mediaUrl ? (
            <button
              type="button"
              className="ss-upload"
              onClick={() => fileInputRef.current?.click()}
            >
              <span className="ss-upload-icon" aria-hidden="true">↑</span>
              <span>{t("sound_upload_prompt")}</span>
              <span className="ss-muted">{t("sound_upload_formats")}</span>
            </button>
          ) : (
            <div className="ss-player">
              <div className="ss-media-wrap">
                {isVideo ? (
                  <video
                    ref={mediaRef}
                    src={mediaUrl}
                    className="ss-video"
                    playsInline
                    onLoadedMetadata={(e) => setDuration(e.target.duration)}
                    onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                  />
                ) : (
                  <audio
                    ref={mediaRef}
                    src={mediaUrl}
                    className="ss-audio-hidden"
                    onLoadedMetadata={(e) => setDuration(e.target.duration)}
                    onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                  />
                )}
                {!isVideo && (
                  <div className="ss-audio-art" aria-hidden="true">
                    <span>🎧</span>
                    <p>{mediaFile?.name || "Audio track"}</p>
                  </div>
                )}
              </div>

              {decodeNote && <p className="ss-muted ss-decode-note">{decodeNote}</p>}
              {saveNotice && <p className="ss-save-notice" role="status">{saveNotice}</p>}

              <SuperSoundSavePanel
                user={user}
                mediaFile={mediaFile}
                isVideo={isVideo}
                folders={folders}
                selectedFolderId={selectedFolderId}
                onSelectFolder={setSelectedFolderId}
                newFolderName={newFolderName}
                onNewFolderNameChange={setNewFolderName}
                onCreateFolder={createFolder}
                onSave={saveCurrentToLibrary}
                saving={saving}
              />

              <div className="ss-controls">
                <button type="button" className="ss-btn" onClick={() => skip(-5)} aria-label="Back 5 seconds">
                  −5s
                </button>
                <button type="button" className="ss-btn ss-btn-primary ss-play" onClick={togglePlay}>
                  {playing ? "Pause" : "Play"}
                </button>
                <button type="button" className="ss-btn" onClick={() => skip(5)} aria-label="Forward 5 seconds">
                  +5s
                </button>
                <span className="ss-time">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <button
                  type="button"
                  className="ss-btn ss-btn-ghost"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {t("sound_open_another")}
                </button>
              </div>
            </div>
          )}

          <div className="ss-waveform-dock">
            <WaveformBar
              peaks={peaks}
              duration={duration}
              currentTime={currentTime}
              onSeek={seekTo}
            />
          </div>

          <div className="ss-tip">
            <strong>{t("sound_meet_tip_title")}</strong>
            <p>{t("sound_meet_tip_body")}</p>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="ss-file-input"
        onChange={onFileChange}
      />
    </div>
  );
}
